import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import type { ChatMessage, Citation, UserPreferences, ChecklistData, BriefSection, BriefProgress, BriefMeta, BriefSectionStatus, SbsComparisonState, SbsJurisdictionResult, TabState } from './types';
import { DEFAULT_PREFERENCES, JURISDICTION_GROUPS } from './types';
import { authFetch, streamQuery, streamResearchBrief, streamComparison, fetchQueryResult, fetchChecklist, fetchSessionDetail, fetchPreferences, submitIssue, fetchCorpusStats, fetchSimilarQuery, fetchTemplates, createTemplate, deleteTemplate } from './api';
import type { SimilarQueryResult, QueryTemplate } from './api';
import { hydrateCorpusStats } from './constants';
import type { ResearchBriefCallbacks, ComparisonCallbacks } from './api';
import Chat from './components/Chat';
import CitationPanel from './components/CitationPanel';
import AuthGate, { useLanding } from './components/AuthGate';
import AppHeader from './components/AppHeader';
import SessionSidebar from './components/SessionSidebar';
import ResourceLinks from './components/ResourceLinks';
import CorpusBrowser from './components/CorpusBrowser';
import Settings, { GearIcon, applyAccent } from './components/Settings';
import QueryInput from './components/QueryInput';
import type { QueryInputHandle } from './components/QueryInput';
import type { Theme, Skin } from './components/Settings';
// Note: Settings is now a sidebar, always rendered, controlled by isOpen prop
import StreamProgress from './components/StreamProgress';
import ResizeDivider from './components/ResizeDivider';
import { loadBookmarks } from './components/Bookmarks';
import OnboardingTour from './components/OnboardingTour';
import type { StreamPhase } from './components/StreamProgress';
import useKeyboardShortcuts from './hooks/useKeyboardShortcuts';
import { exportSession } from './utils/exportSession';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import CommandPalette from './components/CommandPalette';
import SelectionToolbar from './components/SelectionToolbar';
import ReportBuilder from './components/ReportBuilder';
import ManualBuilder from './components/ManualBuilder';
import ManualReview from './components/ManualReview';
import ResearchPanel from './components/ResearchPanel';
import { loadHighlights } from './highlights';
import CompareView from './components/CompareView';
import ResearchBriefView from './components/ResearchBriefView';
import SbsComparisonView, { SbsJurisdictionPicker } from './components/SbsComparisonView';
import { ToastProvider, useToast } from './contexts/ToastContext';
import ToastContainer from './components/Toast';
import TabBar from './components/TabBar';
import InboxModal, { fetchIngestionQueueCount } from './components/InboxModal';
import CostDashboard from './components/CostDashboard';
import AnalyticsDashboard from './components/AnalyticsDashboard';
// ToolbarOverflow removed — bottom toolbar killed (toolbar-kill)
import Sidebar from './components/Sidebar';
import AlertsBanner from './components/AlertsBanner';
import ToolLayout from './components/ToolLayout';

/* ── Tool pages (promoted from modals) ───────────────────── */
const ReportBuilderPage = lazy(() => import('./pages/ReportBuilderPage'));
const CopyBuilderPage    = lazy(() => import('./pages/CopyBuilderPage'));
const ManualReviewPage   = lazy(() => import('./pages/ManualReviewPage'));
const CorpusBrowserPage  = lazy(() => import('./pages/CorpusBrowserPage'));
const ResearchPage       = lazy(() => import('./pages/ResearchPage'));

/* ── Portal lazy-loaded views ─────────────────────────────── */
const PortalLayout = lazy(() => import('./components/portal/PortalLayout'));
const UsageView    = lazy(() => import('./components/portal/UsageView'));
const PhaseView    = lazy(() => import('./components/portal/PhaseView'));
const CorpusView   = lazy(() => import('./components/portal/CorpusView'));
const QueueView    = lazy(() => import('./components/portal/QueueView'));
const KeysView     = lazy(() => import('./components/portal/KeysView'));
const InboxView    = lazy(() => import('./components/portal/InboxView'));
const UsersView    = lazy(() => import('./components/portal/UsersView'));
const AuditView    = lazy(() => import('./components/portal/AuditView'));
const SettingsView    = lazy(() => import('./components/portal/SettingsView'));
const ComplianceView          = lazy(() => import('./components/portal/ComplianceView'));
const DashboardView           = lazy(() => import('./components/portal/DashboardView'));
const ComingSoonView          = lazy(() => import('./components/portal/ComingSoonView'));
const AmendmentAlertsView     = lazy(() => import('./components/portal/AmendmentAlertsView'));
const CorpusLogView           = lazy(() => import('./components/portal/CorpusLogView'));

/* ── Portal routes component ─────────────────────────────── */
function PortalRoutes() {
  const PortalFallback = <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--ink-3, #94a3b8)', fontSize: '14px' }}>Loading portal…</div>;
  return (
    <Routes>
      <Route
        path="/"
        element={
          <Suspense fallback={PortalFallback}>
            <PortalLayout />
          </Suspense>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<Suspense fallback={null}><DashboardView /></Suspense>} />
        <Route path="usage"  element={<Suspense fallback={null}><UsageView /></Suspense>} />
        <Route path="phase"  element={<Suspense fallback={null}><PhaseView /></Suspense>} />
        <Route path="corpus" element={<Suspense fallback={null}><CorpusView /></Suspense>} />
        <Route path="queue"  element={<Suspense fallback={null}><QueueView /></Suspense>} />
        <Route path="keys"   element={<Suspense fallback={null}><KeysView /></Suspense>} />
        <Route path="inbox"    element={<Suspense fallback={null}><InboxView /></Suspense>} />
        <Route path="users"      element={<Suspense fallback={null}><UsersView /></Suspense>} />
        <Route path="audit"      element={<Suspense fallback={null}><AuditView /></Suspense>} />
        <Route path="settings"   element={<Suspense fallback={null}><SettingsView /></Suspense>} />
        <Route path="compliance" element={<Suspense fallback={null}><ComplianceView /></Suspense>} />
        <Route path="amendments" element={<Suspense fallback={null}><AmendmentAlertsView /></Suspense>} />
        <Route path="corpus-log" element={<Suspense fallback={null}><CorpusLogView /></Suspense>} />
        <Route path="coming-soon/:feature" element={<Suspense fallback={null}><ComingSoonView /></Suspense>} />
      </Route>
    </Routes>
  );
}

/* ── localStorage keys ─────────────────────────────── */

const LS_MESSAGES_KEY = 'standards-app-messages';
const LS_QUERY_ID_KEY = 'standards-app-active-query-id';
const LS_SESSION_ID_KEY = 'standards-session-id';
const LS_USER_ID_KEY = 'standards-user-id';
const LS_TABS_KEY = 'standards-app-tabs';
const LS_ACTIVE_TAB_KEY = 'standards-app-active-tab';
const LS_SPLIT_RATIO_KEY = 'standards-split-ratio'; // D4: split pane persistence

/* ── Helpers ─────────────────────────────────────────────────── */

let nextId = 0;
function uid(): string {
  return `msg-${Date.now()}-${++nextId}`;
}

/* ── G2: Tab helpers ─────────────────────────────────────────── */

function createDefaultTab(): TabState {
  return {
    id: crypto.randomUUID(),
    label: 'New Query',
    messages: [],
    sessionId: null,
    activeQueryId: null,
    briefSections: [],
    briefExecutiveSummary: '',
    briefProgress: null,
    briefMeta: null,
    briefError: null,
    briefActive: false,
    sbsState: null,
    sbsActive: false,
    isStreaming: false,
  };
}

function serializeTabsForStorage(tabs: TabState[]): string {
  const serializable = tabs.map((tab) => ({
    ...tab,
    isStreaming: false, // never persist streaming state
    messages: tab.messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
    // SbsComparisonState.results is a Map — convert to entry array for JSON
    sbsState: tab.sbsState
      ? { ...tab.sbsState, results: Array.from(tab.sbsState.results.entries()) }
      : null,
  }));
  return JSON.stringify(serializable);
}

function deserializeTabsFromStorage(raw: string): TabState[] {
  const parsed = JSON.parse(raw);
  if (!Array.isArray(parsed) || parsed.length === 0) return [createDefaultTab()];
  return parsed.map((tab: Record<string, unknown>) => {
    const messages = Array.isArray(tab.messages) ? (tab.messages as ChatMessage[]) : [];
    // Reset orphaned tabs: label set but no messages means stale state
    const label = (messages.length === 0 && String(tab.label ?? 'New Query') !== 'New Query')
      ? 'New Query'
      : String(tab.label ?? 'New Query');
    return {
    id: String(tab.id ?? crypto.randomUUID()),
    label,
    messages,
    sessionId: (tab.sessionId as string | null) ?? null,
    activeQueryId: null, // stream is dead after reload
    briefSections: Array.isArray(tab.briefSections) ? (tab.briefSections as BriefSection[]) : [],
    briefExecutiveSummary: String(tab.briefExecutiveSummary ?? ''),
    briefProgress: (tab.briefProgress as BriefProgress | null) ?? null,
    briefMeta: (tab.briefMeta as BriefMeta | null) ?? null,
    briefError: (tab.briefError as string | null) ?? null,
    briefActive: Boolean(tab.briefActive),
    sbsState: tab.sbsState
      ? {
          ...(tab.sbsState as Omit<SbsComparisonState, 'results'>),
          results: new Map(
            Array.isArray((tab.sbsState as { results?: unknown }).results)
              ? (tab.sbsState as { results: [string, SbsJurisdictionResult][] }).results
              : [],
          ),
        }
      : null,
    sbsActive: Boolean(tab.sbsActive),
    isStreaming: false, // never restore streaming state
  };
  });
}

// Module-level cache so both useState initializers share the same initial object
// when localStorage is empty (avoids two createDefaultTab() calls with different IDs).
let _tabsInitCache: { tabs: TabState[]; activeTabId: string } | undefined;
function getTabsInitialState(): { tabs: TabState[]; activeTabId: string } {
  if (_tabsInitCache) return _tabsInitCache;
  try {
    const stored = localStorage.getItem(LS_TABS_KEY);
    if (stored) {
      const parsed = deserializeTabsFromStorage(stored);
      if (parsed.length > 0) {
        // Restore the last active tab, or fall back to a tab with messages, or first tab
        const savedActiveId = localStorage.getItem(LS_ACTIVE_TAB_KEY);
        const restoredActive = savedActiveId && parsed.find(t => t.id === savedActiveId)
          ? savedActiveId
          : (parsed.find(t => t.messages.length > 0)?.id ?? parsed[0].id);
        _tabsInitCache = { tabs: parsed, activeTabId: restoredActive };
        return _tabsInitCache;
      }
    }
  } catch { /* ignore */ }
  const defaultTab = createDefaultTab();
  _tabsInitCache = { tabs: [defaultTab], activeTabId: defaultTab.id };
  return _tabsInitCache;
}

/* ── App ─────────────────────────────────────────────────────── */

function MainApp() {
  // G2: restore messages from the active tab in localStorage (if any)
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.messages ?? [];
  });
  const { addToast } = useToast();
  const addToastRef = useRef(addToast);
  useEffect(() => { addToastRef.current = addToast; }, [addToast]);

  const [isStreaming, setIsStreaming] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showCorpusBrowser, setShowCorpusBrowser] = useState(false);
  const [corpusBrowserTab, setCorpusBrowserTab] = useState<'browse' | 'updates'>('browse');
  const [showSettings, setShowSettings] = useState(false);
  const [showResearch, setShowResearch] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [researchTab, setResearchTab] = useState<'bookmarks' | 'highlights'>('bookmarks');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [showOverflow, setShowOverflow] = useState(false);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [showManualBuilder, setShowManualBuilder] = useState(false);
  const [showManualReview, setShowManualReview] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showResourceLinks, setShowResourceLinks] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(() => loadBookmarks().length);
  const [highlightCount, setHighlightCount] = useState(() => loadHighlights().length);
  const [highlightRefreshKey, setHighlightRefreshKey] = useState(0);
  const [tourKey, setTourKey] = useState(0);
  const [showTourForce, setShowTourForce] = useState(false);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);

  // Portal navigation
  const navigate = useNavigate();

  // B1: Admin inbox state
  const isAdminUser = localStorage.getItem('standards-user-role') === 'admin';

  const [showInbox, setShowInbox] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [showCostDashboard, setShowCostDashboard] = useState(false);
  const [showAnalyticsDashboard, setShowAnalyticsDashboard] = useState(false);

  /* Beta banner — modal on first load, amber top bar after dismiss */
  const [betaBannerGone, setBetaBannerGone] = useState(() => sessionStorage.getItem('beta-banner-dismissed') === 'true');
  const [betaBannerDismissing, setBetaBannerDismissing] = useState(false);
  const queryInputHandleRef = useRef<QueryInputHandle>(null);
  const [streamPhase, setStreamPhase] = useState<StreamPhase>('idle');
  // D4: Split ratio — persisted to localStorage so the user's preferred width survives reloads
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    try {
      const stored = localStorage.getItem('standards-split-ratio');
      if (stored) {
        const val = parseFloat(stored);
        if (!isNaN(val) && val >= 0.25 && val <= 0.75) return val;
      }
    } catch { /* ignore */ }
    return 0.5;
  });

  // G5: Similar query suggestion banner
  const [similarBanner, setSimilarBanner] = useState<{ result: SimilarQueryResult; pendingQuery: string } | null>(null);
  const bypassSimilarRef = useRef(false);
  // G5-fix: set to true by "Ask fresh anyway" so the worker skips the G4 cache
  const skipCacheRef = useRef(false);

  // Phase 8: User preferences — domain filters + plain english mode
  const [userPrefs, setUserPrefs] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [activeDomains, setActiveDomains] = useState<string[]>([]);

  // Checklist mode
  const [checklistMode, setChecklistMode] = useState(false);

  // Research Brief mode
  const [researchBriefMode, setResearchBriefMode] = useState(false);
  const [briefSections, setBriefSections] = useState<BriefSection[]>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefSections ?? [];
  });
  const [briefExecutiveSummary, setBriefExecutiveSummary] = useState<string>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefExecutiveSummary ?? '';
  });
  const [briefProgress, setBriefProgress] = useState<BriefProgress | null>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefProgress ?? null;
  });
  const [briefMeta, setBriefMeta] = useState<BriefMeta | null>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefMeta ?? null;
  });
  const [briefError, setBriefError] = useState<string | null>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefError ?? null;
  });
  const [briefActive, setBriefActive] = useState<boolean>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.briefActive ?? false;
  });

  // Side-by-Side Comparison mode
  const [sbsMode, setSbsMode] = useState(false);
  const [sbsSelectedJurisdictions, setSbsSelectedJurisdictions] = useState<string[]>([]);
  const [sbsState, setSbsState] = useState<SbsComparisonState | null>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.sbsState ?? null;
  });
  const [sbsActive, setSbsActive] = useState<boolean>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.sbsActive ?? false;
  });

  // Query template library state
  const [savedTemplates, setSavedTemplates] = useState<QueryTemplate[]>([]);

  // Load preferences on mount
  useEffect(() => {
    fetchPreferences().then((prefs) => {
      setUserPrefs(prefs);
      setActiveDomains(prefs.default_domains);
    });
  }, []);

  // Load saved query templates on mount
  useEffect(() => {
    fetchTemplates().then(setSavedTemplates);
  }, []);

  // A6: hydrate the single-source-of-truth corpus counts from the live public
  // endpoint on boot. Falls back silently to build-time CORPUS_STATS on failure.
  useEffect(() => {
    fetchCorpusStats().then((live) => { if (live) hydrateCorpusStats(live); });
  }, []);

  const handlePreferencesChange = useCallback((prefs: UserPreferences) => {
    setUserPrefs(prefs);
    setActiveDomains(prefs.default_domains);
  }, []);

  // Ref to the query textarea for keyboard shortcuts
  const queryInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Lazy-bind: Chat renders QueryInput which has the textarea
  useEffect(() => {
    queryInputRef.current = document.querySelector('.query-textarea') as HTMLTextAreaElement | null;
  });

  const [userId] = useState<string>(() => {
    try {
      const stored = localStorage.getItem(LS_USER_ID_KEY);
      if (stored) return stored;
      const newId = crypto.randomUUID();
      localStorage.setItem(LS_USER_ID_KEY, newId);
      return newId;
    } catch { return 'anonymous'; }
  });

  // G2: restore session ID from active tab (enables conversation continuity)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
    const init = getTabsInitialState();
    return init.tabs.find((t) => t.id === init.activeTabId)?.sessionId ?? null;
  });

  useEffect(() => {
    try {
      if (currentSessionId) localStorage.setItem(LS_SESSION_ID_KEY, currentSessionId);
      else localStorage.removeItem(LS_SESSION_ID_KEY);
    } catch { /* ignore */ }
  }, [currentSessionId]);

  // ── G2: Tab state ────────────────────────────────────────────
  const [tabs, setTabs] = useState<TabState[]>(() => getTabsInitialState().tabs);
  const [activeTabId, setActiveTabId] = useState<string>(() => getTabsInitialState().activeTabId);

  const [activeCitationNum, setActiveCitationNum] = useState<number | null>(null);
  const [activeMsgId, setActiveMsgId] = useState<string | null>(null);
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Notification dot for Corpus Updates tab
  const hasUnreadUpdates = useMemo(() => {
    try {
      const lastSeen = localStorage.getItem('ch-corpus-updates-seen');
      return !lastSeen || new Date(lastSeen) < new Date('2026-06-13');
    } catch { return false; }
  }, []);

  // Abort controller ref — allows the Stop button to cancel in-flight streams
  const abortRef = useRef<AbortController | null>(null);

  // Active query ID — start fresh, no restore
  const [activeQueryId, setActiveQueryId] = useState<string | null>(null);
  const activeQueryIdRef = useRef<string | null>(activeQueryId);

  // G2: Refs for activeTabId and current tab label — used in callbacks without deps
  const activeTabIdRef = useRef<string>(activeTabId);
  const activeTabLabelRef = useRef<string>(
    tabs.find((t) => t.id === activeTabId)?.label ?? 'New Query'
  );
  useEffect(() => { activeTabIdRef.current = activeTabId; }, [activeTabId]);
  useEffect(() => {
    activeTabLabelRef.current = tabs.find((t) => t.id === activeTabId)?.label ?? 'New Query';
  }, [tabs, activeTabId]);

  // Wake lock sentinel ref
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // Track last chunk time for dead-stream detection
  const lastChunkTimeRef = useRef<number>(Date.now());

  // C3: bottom-sheet swipe-to-dismiss refs
  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const swipeStartYRef = useRef<number>(0);

  // ── Persist messages to localStorage whenever they change ──
  useEffect(() => {
    try {
      // Strip isStreaming flag from persisted messages so on-reload
      // all messages appear complete
      const toStore = messages.map((m) =>
        m.isStreaming ? { ...m, isStreaming: false } : m
      );
      localStorage.setItem(LS_MESSAGES_KEY, JSON.stringify(toStore));
    } catch { /* quota errors ignored */ }
  }, [messages]);

  // ── G2: Persist full tabs array to localStorage ──
  useEffect(() => {
    try {
      localStorage.setItem(LS_TABS_KEY, serializeTabsForStorage(tabs));
    } catch { /* quota errors ignored */ }
  }, [tabs]);

  // Persist active tab ID
  useEffect(() => {
    try { localStorage.setItem(LS_ACTIVE_TAB_KEY, activeTabId); } catch { /* ignore */ }
  }, [activeTabId]);

  // ── D4: Persist split ratio to localStorage ──
  useEffect(() => {
    try {
      localStorage.setItem(LS_SPLIT_RATIO_KEY, String(splitRatio));
    } catch { /* ignore */ }
  }, [splitRatio]);

  // ── Persist activeQueryId to localStorage ──
  useEffect(() => {
    activeQueryIdRef.current = activeQueryId;
    try {
      if (activeQueryId) {
        localStorage.setItem(LS_QUERY_ID_KEY, activeQueryId);
      } else {
        localStorage.removeItem(LS_QUERY_ID_KEY);
      }
    } catch { /* ignore */ }
  }, [activeQueryId]);

  // ── visibilitychange handler: recover if stream appears dead ──
  const isStreamingRef = useRef(isStreaming);
  useEffect(() => { isStreamingRef.current = isStreaming; }, [isStreaming]);

  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!isStreamingRef.current) return;

      const qid = activeQueryIdRef.current;
      if (!qid) return;

      // Only attempt recovery if stream has been quiet for 3+ seconds
      const silentMs = Date.now() - lastChunkTimeRef.current;
      if (silentMs < 3000) return;

      try {
        const result = await fetchQueryResult(qid);
        if (result.status === 'complete' && result.response_text) {
          addToastRef.current('info', 'Reconnected — loading response…', 3000);
          // Patch the last streaming assistant message with full text + citations
          setMessages((prev) => {
            const lastAssistantIdx = [...prev].reverse().findIndex(
              (m) => m.role === 'assistant' && m.isStreaming
            );
            if (lastAssistantIdx === -1) return prev;
            const realIdx = prev.length - 1 - lastAssistantIdx;

            const citations: Citation[] = (result.citations ?? []).map((c, i) => ({
              id: c.id,
              number: i + 1,
              standard: c.standard,
              clause: c.clause_number ?? '',
              text: '',
              provenance: c.provenance ?? null, // G7: preserve HTML provenance
            }));

            return prev.map((m, idx) =>
              idx === realIdx
                ? { ...m, content: result.response_text!, citations, isStreaming: false, retrievalTree: m.retrievalTree ?? result.retrieval_tree ?? null }
                : m
            );
          });
          setIsStreaming(false);
          setActiveQueryId(null);
          abortRef.current?.abort();
          abortRef.current = null;
          // Release wake lock if held
          if (wakeLockRef.current) {
            try { await wakeLockRef.current.release(); } catch { /* ignore */ }
            wakeLockRef.current = null;
          }
        }
      } catch (e) {
        console.warn('Recovery fetch failed:', e);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    // Active-tab dead stream watchdog: check every 5s while streaming
    const watchdog = setInterval(() => {
      if (!isStreamingRef.current) return;
      const silentMs = Date.now() - lastChunkTimeRef.current;
      if (silentMs > 15000) {
        // Stream appears dead for 15s while tab is focused — attempt recovery
        handleVisibility();
      }
    }, 5000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(watchdog);
    };
  }, []);

  /* ── Derived: which citations to display in the panel ── */
  // ── Back button: abort streaming + handle navigation ──
  useEffect(() => {
    const handlePopState = () => {
      if (isStreamingRef.current) {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsStreaming(false);
        setActiveQueryId(null);
        setMessages((prev) =>
          prev.map((m) => m.isStreaming ? { ...m, isStreaming: false } : m)
        );
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const panelCitations = useMemo<Citation[]>(() => {
    // Prefer citations from the explicitly-selected message
    if (activeMsgId) {
      const msg = messages.find((m) => m.id === activeMsgId);
      if (msg && msg.citations.length > 0) return msg.citations;
    }
    // Fallback: latest assistant message with citations
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && m.citations.length > 0) return m.citations;
    }
    return [];
  }, [messages, activeMsgId]);

  /* R2-H4: Track the query text for the active citation set */
  const panelQueryText = useMemo<string | undefined>(() => {
    // Find the assistant message whose citations are displayed
    let targetMsgIdx = -1;
    if (activeMsgId) {
      const idx = messages.findIndex((m) => m.id === activeMsgId);
      if (idx >= 0 && messages[idx].citations.length > 0) targetMsgIdx = idx;
    }
    if (targetMsgIdx === -1) {
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === 'assistant' && messages[i].citations.length > 0) {
          targetMsgIdx = i;
          break;
        }
      }
    }
    // The user message is typically right before the assistant message
    if (targetMsgIdx > 0 && messages[targetMsgIdx - 1].role === 'user') {
      return messages[targetMsgIdx - 1].content;
    }
    return undefined;
  }, [messages, activeMsgId]);

  /* ── Retrieval tree for the active citation set ── */
  const panelRetrievalTree = useMemo(() => {
    // Find the same assistant message used for panelCitations
    if (activeMsgId) {
      const msg = messages.find((m) => m.id === activeMsgId);
      if (msg && msg.retrievalTree) return msg.retrievalTree;
    }
    // Fallback: latest assistant message with a retrieval tree
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && m.retrievalTree) return m.retrievalTree;
    }
    return null;
  }, [messages, activeMsgId]);

  /* ── Query count for header ── */
  const queryCount = useMemo(
    () => messages.filter((m) => m.role === 'user').length,
    [messages],
  );

  // B1: Poll inbox badge count — visible to all users (Grant directive 2026-06-20)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const count = await fetchIngestionQueueCount();
      if (!cancelled) setInboxCount(count);
    };
    void poll();
    const interval = setInterval(() => { void poll(); }, 60_000); // every minute
    return () => { cancelled = true; clearInterval(interval); };
  }, [isAdminUser]);


  /* ── G2: Create a new tab (or reset current if already empty) ── */
  const handleNewTab = useCallback(() => {
    if (tabs.length >= 10) {
      addToastRef.current('warning', 'Maximum 10 tabs reached', 3000);
      return;
    }
    // Abort any in-progress stream
    if (isStreaming) {
      abortRef.current?.abort();
      abortRef.current = null;
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    }
    // Snapshot current tab
    const snapshot: TabState = {
      id: activeTabId,
      label: tabs.find((t) => t.id === activeTabId)?.label ?? 'New Query',
      messages: messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
      sessionId: currentSessionId,
      activeQueryId: null,
      briefSections: briefSections.map((s) => ({ ...s, isStreaming: false })),
      briefExecutiveSummary,
      briefProgress,
      briefMeta,
      briefError,
      briefActive,
      sbsState,
      sbsActive,
      isStreaming: false,
    };
    const newTab = createDefaultTab();
    setTabs((prev) => [...prev.map((t) => (t.id === activeTabId ? snapshot : t)), newTab]);
    setActiveTabId(newTab.id);
    // Reset all state for the new empty tab
    setMessages([]);
    setCurrentSessionId(null);
    setActiveQueryId(null);
    setBriefSections([]);
    setBriefExecutiveSummary('');
    setBriefProgress(null);
    setBriefMeta(null);
    setBriefError(null);
    setBriefActive(false);
    setSbsState(null);
    setSbsActive(false);
    setIsStreaming(false);
    setStreamPhase('idle');
    setActiveMsgId(null);
    setActiveCitationNum(null);
    setResearchBriefMode(false);
    if (window.location.pathname !== '/') {
      window.history.pushState(null, '', '/');
    }
  }, [
    tabs, activeTabId, isStreaming, messages, currentSessionId,
    briefSections, briefExecutiveSummary, briefProgress, briefMeta,
    briefError, briefActive, sbsState, sbsActive,
  ]);

  /* ── G2: Select an existing tab ── */
  const handleSelectTab = useCallback((tabId: string) => {
    if (tabId === activeTabId) return;
    // Abort streaming before switching
    if (isStreaming) {
      abortRef.current?.abort();
      abortRef.current = null;
      setIsStreaming(false);
      setStreamPhase('idle');
      setActiveQueryId(null);
      if (briefActive) {
        setBriefSections((prev) => prev.map((s) => ({ ...s, isStreaming: false })));
        setBriefProgress((prev) => (prev ? { ...prev, phase: 'complete' } : null));
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
    }
    const snapshot: TabState = {
      id: activeTabId,
      label: tabs.find((t) => t.id === activeTabId)?.label ?? 'New Query',
      messages: messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
      sessionId: currentSessionId,
      activeQueryId: null,
      briefSections: briefSections.map((s) => ({ ...s, isStreaming: false })),
      briefExecutiveSummary,
      briefProgress,
      briefMeta,
      briefError,
      briefActive,
      sbsState,
      sbsActive,
      isStreaming: false,
    };
    const targetTab = tabs.find((t) => t.id === tabId);
    if (!targetTab) return;
    setTabs((prev) => prev.map((t) => (t.id === activeTabId ? snapshot : t)));
    setActiveTabId(tabId);
    setMessages(targetTab.messages);
    setCurrentSessionId(targetTab.sessionId);
    setActiveQueryId(targetTab.activeQueryId);
    setBriefSections(targetTab.briefSections);
    setBriefExecutiveSummary(targetTab.briefExecutiveSummary);
    setBriefProgress(targetTab.briefProgress);
    setBriefMeta(targetTab.briefMeta);
    setBriefError(targetTab.briefError);
    setBriefActive(targetTab.briefActive);
    setSbsState(targetTab.sbsState);
    setSbsActive(targetTab.sbsActive);
    setIsStreaming(false); // stream is dead — never restore streaming
    setStreamPhase('idle');
    setActiveMsgId(null);
    setActiveCitationNum(null);
  }, [
    activeTabId, tabs, isStreaming, messages, currentSessionId,
    briefSections, briefExecutiveSummary, briefProgress, briefMeta,
    briefError, briefActive, sbsState, sbsActive,
  ]);

  /* ── G2: Close a tab ── */
  const handleCloseTab = useCallback((tabId: string) => {
    const tabIndex = tabs.findIndex((t) => t.id === tabId);
    if (tabIndex === -1) return;

    if (tabs.length <= 1) {
      // Last tab — reset it to a fresh empty state instead of removing
      const freshTab = createDefaultTab();
      setTabs([freshTab]);
      setActiveTabId(freshTab.id);
      setMessages([]);
      setCurrentSessionId(null);
      setActiveQueryId(null);
      setBriefSections([]);
      setBriefExecutiveSummary('');
      setBriefProgress(null);
      setBriefMeta(null);
      setBriefError(null);
      setBriefActive(false);
      setSbsState(null);
      setSbsActive(false);
      setIsStreaming(false);
      setStreamPhase('idle');
      setActiveMsgId(null);
      setActiveCitationNum(null);
      setResearchBriefMode(false);
      if (window.location.pathname !== '/') {
        window.history.pushState(null, '', '/');
      }
      return;
    }

    const newTabs = tabs.filter((t) => t.id !== tabId);
    if (tabId === activeTabId) {
      // Closing the active tab — switch to adjacent
      const newActive = newTabs[Math.max(0, tabIndex - 1)];
      setTabs(newTabs);
      setActiveTabId(newActive.id);
      setMessages(newActive.messages);
      setCurrentSessionId(newActive.sessionId);
      setActiveQueryId(newActive.activeQueryId);
      setBriefSections(newActive.briefSections);
      setBriefExecutiveSummary(newActive.briefExecutiveSummary);
      setBriefProgress(newActive.briefProgress);
      setBriefMeta(newActive.briefMeta);
      setBriefError(newActive.briefError);
      setBriefActive(newActive.briefActive);
      setSbsState(newActive.sbsState);
      setSbsActive(newActive.sbsActive);
      setIsStreaming(false);
      setStreamPhase('idle');
      setActiveMsgId(null);
      setActiveCitationNum(null);
    } else {
      setTabs(newTabs);
    }
  }, [tabs, activeTabId]);

  /* ── New chat: create new tab if there are messages, else reset current ── */
  const handleNewChat = useCallback(() => {
    if (messages.length > 0 || briefActive || sbsActive) {
      handleNewTab();
    } else {
      // Already empty — just reset UI state without creating a new tab
      abortRef.current?.abort();
      abortRef.current = null;
      setIsStreaming(false);
      setStreamPhase('idle');
      setActiveQueryId(null);
      setActiveMsgId(null);
      setActiveCitationNum(null);
      setResearchBriefMode(false);
      if (window.location.pathname !== '/') {
        window.history.pushState(null, '', '/');
      }
    }
  }, [messages.length, briefActive, sbsActive, handleNewTab]);

  /* ── Clear chat: reset current tab without creating a new one ── */
  const handleClearChat = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    setMessages([]);
    setIsStreaming(false);
    setStreamPhase('idle');
    setActiveQueryId(null);
    setActiveMsgId(null);
    setActiveCitationNum(null);
    setResearchBriefMode(false);
    setBriefActive(false);
    setSbsActive(false);
    // Update the tab label back to default
    setTabs(prev => prev.map(t =>
      t.id === activeTabId ? { ...t, label: 'New Query', messages: [], isStreaming: false } : t
    ));
    if (window.location.pathname !== '/') {
      window.history.pushState(null, '', '/');
    }
  }, [activeTabId]);

  /* ── Load a previous session ── */
  const handleLoadSession = useCallback(async (sessionId: string) => {
    try {
      const { messages: msgs, checklists, current_corpus_version: currentCorpusVersion } = await fetchSessionDetail(sessionId);
      const loaded: ChatMessage[] = msgs.map((m, i) => {
        // Restore citations from DB if available
        let citations: Citation[] = [];
        if (m.citations_json) {
          try {
            const parsed = JSON.parse(m.citations_json);
            if (Array.isArray(parsed)) {
              citations = parsed.map((c: any, ci: number) => ({
                id: c.id || `restored-${ci}`,
                number: ci + 1,
                standard: c.standard || '',
                clause: c.clause_number || c.clause || '',
                text: c.body || c.text || '',
                skill: c.skill || '',
                provenance: c.provenance ?? null, // G7: HTML source badge
              }));
            }
          } catch { /* ignore parse errors */ }
        }
        // Restore checklist from D1 if this message has one
        const checklist = (m.id && checklists[m.id]) ? checklists[m.id] : undefined;
        // F35: replay the retrieval map stored with the query
        let retrievalTree: ChatMessage['retrievalTree'] = null;
        if (m.retrieval_tree_json) {
          try { retrievalTree = JSON.parse(m.retrieval_tree_json); } catch { retrievalTree = null; }
        }
        return {
          id: m.id || `loaded-${i}`,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          citations,
          isStreaming: false,
          checklist,
          queryId: m.query_id ?? undefined,
          retrievalTree,
          confidence: (m.confidence as ChatMessage['confidence']) ?? undefined,
          confidenceReason: m.confidence_reason ?? undefined,
          corpusVersion: m.corpus_version ?? undefined,
          corpusStale: !!(m.corpus_version && currentCorpusVersion && m.corpus_version !== currentCorpusVersion),
        };
      });
      setMessages(loaded);
      setCurrentSessionId(sessionId);
      // G2: Update the active tab label from the session's first user message
      const firstUserMsg = loaded.find((m) => m.role === 'user');
      if (firstUserMsg) {
        const tabId = activeTabIdRef.current;
        const label = firstUserMsg.content.trim().slice(0, 40) || 'Session';
        setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label } : t)));
      }
      setActiveMsgId(null);
      setActiveCitationNum(null);
    } catch {
      /* ignore */
    }
  }, []);

  /* ── Stop an in-progress stream ── */
  const handleStop = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setStreamPhase('idle');
    setActiveQueryId(null);
    // Mark the last assistant message as done
    setMessages((prev) => {
      const last = [...prev].reverse().find((m) => m.role === 'assistant' && m.isStreaming);
      if (!last) return prev;
      return prev.map((m) =>
        m.id === last.id ? { ...m, isStreaming: false } : m,
      );
    });
    // Stop research brief if active
    if (briefActive) {
      setBriefProgress((prev) => prev ? { ...prev, phase: 'complete' } : null);
      setBriefSections((prev) => prev.map((s) => ({ ...s, isStreaming: false })));
    }
    // Release wake lock
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch { /* ignore */ }
      wakeLockRef.current = null;
    }
  }, [briefActive]);

  /* ── Send a Research Brief ── */
  const handleSendResearchBrief = useCallback(
    async (query: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setShowCommandPalette(false);

      // Reset brief state
      setBriefSections([]);
      setBriefExecutiveSummary('');
      setBriefError(null);
      setBriefActive(true);
      setBriefProgress({
        phase: 'decomposing',
        sections: [],
        overallPercent: 5,
      });
      setBriefMeta({
        title: 'Research Brief',
        briefId: '',
        generatedAt: new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
        totalCitations: 0,
        sectionCount: 0,
        queryText: query,
      });

      // G2: Auto-label current tab from first user message
      if (activeTabLabelRef.current === 'New Query' && query.trim()) {
        const tabId = activeTabIdRef.current;
        const label = query.trim().slice(0, 40);
        setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label } : t)));
      }

      // Add user message to chat thread
      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: query,
        citations: [],
        isStreaming: false,
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsStreaming(true);

      const callbacks: ResearchBriefCallbacks = {
        onDecomposition: (sections) => {
          const newSections: BriefSection[] = sections.map((s) => ({
            id: s.id,
            title: s.title,
            query: s.query,
            content: '',
            citations: [],
            isComplete: false,
            isStreaming: false,
            status: 'queued' as BriefSectionStatus,
          }));
          setBriefSections(newSections);
          setBriefProgress((prev) => prev ? {
            ...prev,
            phase: 'researching',
            overallPercent: 10,
            sections: sections.map((s) => ({
              id: s.id,
              title: s.title,
              status: 'queued' as BriefSectionStatus,
              citationCount: 0,
            })),
          } : null);
          setBriefMeta((prev) => prev ? { ...prev, sectionCount: sections.length } : null);
        },
        onSectionStart: (index, title) => {
          setBriefSections((prev) => prev.map((s) =>
            s.id === index ? { ...s, status: 'searching', isStreaming: true, title: title || s.title } : s
          ));
          setBriefProgress((prev) => {
            if (!prev) return null;
            const total = prev.sections.length;
            const completedBefore = prev.sections.filter(s => s.status === 'complete').length;
            return {
              ...prev,
              overallPercent: Math.round(10 + (completedBefore / Math.max(total, 1)) * 80),
              sections: prev.sections.map((s) =>
                s.id === index ? { ...s, status: 'searching' as BriefSectionStatus, title: title || s.title } : s
              ),
            };
          });
        },
        onSectionText: (index, content) => {
          setBriefSections((prev) => prev.map((s) =>
            s.id === index ? { ...s, content: s.content + content, status: 'writing', isStreaming: true } : s
          ));
          setBriefProgress((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              sections: prev.sections.map((s) =>
                s.id === index ? { ...s, status: 'writing' as BriefSectionStatus } : s
              ),
            };
          });
        },
        onSectionCitations: (index, citations) => {
          setBriefSections((prev) => prev.map((s) =>
            s.id === index ? { ...s, citations: [...s.citations, ...citations] } : s
          ));
          setBriefProgress((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              sections: prev.sections.map((s) =>
                s.id === index ? { ...s, citationCount: s.citationCount + citations.length } : s
              ),
            };
          });
        },
        onSectionDone: (index) => {
          setBriefSections((prev) => prev.map((s) =>
            s.id === index ? { ...s, isComplete: true, isStreaming: false, status: 'complete' } : s
          ));
          setBriefProgress((prev) => {
            if (!prev) return null;
            const updated = prev.sections.map((s) =>
              s.id === index ? { ...s, status: 'complete' as BriefSectionStatus } : s
            );
            const completed = updated.filter(s => s.status === 'complete').length;
            const total = updated.length;
            return {
              ...prev,
              overallPercent: Math.round(10 + (completed / Math.max(total, 1)) * 80),
              sections: updated,
            };
          });
        },
        onAssemblyStart: () => {
          setBriefProgress((prev) => prev ? {
            ...prev,
            phase: 'synthesising',
            overallPercent: 90,
          } : null);
        },
        onSynthesisText: (content) => {
          setBriefExecutiveSummary((prev) => prev + content);
        },
        onDone: (briefId) => {
          setBriefProgress((prev) => prev ? {
            ...prev,
            phase: 'complete',
            overallPercent: 100,
          } : null);
          setBriefMeta((prev) => prev ? { ...prev, briefId: briefId || prev.briefId } : null);
          setIsStreaming(false);
          abortRef.current = null;
        },
        onError: (error) => {
          setBriefError(error);
          setBriefProgress((prev) => prev ? { ...prev, phase: 'complete' } : null);
          setIsStreaming(false);
          abortRef.current = null;
        },
        onStatusUpdate: (status) => {
          setBriefProgress((prev) => prev ? { ...prev, statusText: status } : null);
        },
      };

      try {
        await streamResearchBrief(
          query,
          callbacks,
          controller.signal,
          currentSessionId,
          activeDomains.length > 0 ? activeDomains : undefined,
        );
      } catch {
        setBriefError('Failed to connect to server');
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [currentSessionId, activeDomains],
  );

  /* ── Send a SBS comparison ── */
  const handleSendComparison = useCallback(
    async (query: string) => {
      if (sbsSelectedJurisdictions.length < 2) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setShowCommandPalette(false);

      // Init comparison state
      const initialResults = new Map<string, SbsJurisdictionResult>();
      for (const jid of sbsSelectedJurisdictions) {
        const group = JURISDICTION_GROUPS.flatMap(g => g.targets).find(t => t.id === jid);
        initialResults.set(jid, {
          jurisdictionId: jid,
          label: group?.label ?? jid,
          status: 'queued',
          content: '',
          citations: [],
        });
      }
      const newState: SbsComparisonState = {
        comparisonId: null,
        query,
        selectedJurisdictions: [...sbsSelectedJurisdictions],
        results: initialResults,
        synthesis: '',
        synthesisStatus: 'idle',
        error: null,
      };
      setSbsState(newState);
      setSbsActive(true);

      // G2: Auto-label current tab from first user message
      if (activeTabLabelRef.current === 'New Query' && query.trim()) {
        const tabId = activeTabIdRef.current;
        const label = query.trim().slice(0, 40);
        setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label } : t)));
      }

      // Add user message
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: `⚖️ **Compare:** ${query}\n\n*Jurisdictions: ${sbsSelectedJurisdictions.map(jid => JURISDICTION_GROUPS.flatMap(g => g.targets).find(t => t.id === jid)?.label ?? jid).join(', ')}*`,
        citations: [],
        isStreaming: false,
      };
      setMessages(prev => [...prev, userMsg]);
      setIsStreaming(true);

      const callbacks: ComparisonCallbacks = {
        onComparisonStart: (comparisonId, _jurisdictions) => {
          setSbsState(prev => prev ? { ...prev, comparisonId } : prev);
        },
        onJurisdictionStart: (jurisdictionId) => {
          setSbsState(prev => {
            if (!prev) return prev;
            const results = new Map(prev.results);
            const existing = results.get(jurisdictionId);
            if (existing) results.set(jurisdictionId, { ...existing, status: 'streaming' });
            return { ...prev, results };
          });
        },
        onJurisdictionText: (jurisdictionId, text) => {
          setSbsState(prev => {
            if (!prev) return prev;
            const results = new Map(prev.results);
            const existing = results.get(jurisdictionId);
            if (existing) results.set(jurisdictionId, { ...existing, content: existing.content + text, status: 'streaming' });
            return { ...prev, results };
          });
        },
        onJurisdictionCitations: (jurisdictionId, citations) => {
          setSbsState(prev => {
            if (!prev) return prev;
            const results = new Map(prev.results);
            const existing = results.get(jurisdictionId);
            if (existing) results.set(jurisdictionId, { ...existing, citations });
            return { ...prev, results };
          });
        },
        onJurisdictionDone: (jurisdictionId, confidence, confidenceReason) => {
          setSbsState(prev => {
            if (!prev) return prev;
            const results = new Map(prev.results);
            const existing = results.get(jurisdictionId);
            if (existing) results.set(jurisdictionId, { ...existing, status: 'complete', confidence, confidenceReason });
            return { ...prev, results };
          });
        },
        onJurisdictionError: (jurisdictionId, error) => {
          setSbsState(prev => {
            if (!prev) return prev;
            const results = new Map(prev.results);
            const existing = results.get(jurisdictionId);
            if (existing) results.set(jurisdictionId, { ...existing, status: 'error', error });
            return { ...prev, results };
          });
        },
        onSynthesisStart: () => {
          setSbsState(prev => prev ? { ...prev, synthesisStatus: 'generating' } : prev);
        },
        onSynthesisText: (text) => {
          setSbsState(prev => prev ? { ...prev, synthesis: prev.synthesis + text } : prev);
        },
        onSynthesisDone: (confidence) => {
          setSbsState(prev => prev ? { ...prev, synthesisStatus: 'complete', synthesisConfidence: confidence } : prev);
        },
        onUsage: (_data) => {
          // Could display usage stats if needed
        },
        onDone: () => {
          setIsStreaming(false);
          abortRef.current = null;
        },
        onError: (error) => {
          setSbsState(prev => prev ? { ...prev, error } : prev);
          setIsStreaming(false);
          abortRef.current = null;
        },
      };

      try {
        await streamComparison(
          query,
          sbsSelectedJurisdictions,
          callbacks,
          controller.signal,
          currentSessionId,
        );
      } catch {
        setSbsState(prev => prev ? { ...prev, error: 'Failed to connect to server' } : prev);
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [currentSessionId, sbsSelectedJurisdictions],
  );

  /* ── Send a query ── */
  const handleSend = useCallback(
    async (query: string) => {
      // If research brief mode, redirect to brief handler
      if (researchBriefMode) {
        handleSendResearchBrief(query);
        return;
      }

      // If SBS comparison mode with selected jurisdictions, redirect
      if (sbsMode && sbsSelectedJurisdictions.length >= 2) {
        handleSendComparison(query);
        return;
      }

      // G5: Check for similar recent queries before proceeding (skip if bypassed)
      if (!bypassSimilarRef.current && query.trim().length >= 10) {
        try {
          const similar = await fetchSimilarQuery(query.trim());
          if (similar.found) {
            setSimilarBanner({ result: similar, pendingQuery: query });
            return; // Pause — let user decide
          }
        } catch { /* ignore */ }
      }
      bypassSimilarRef.current = false;
      // Capture and reset skip-cache flag before any async work
      const shouldSkipCache = skipCacheRef.current;
      skipCacheRef.current = false;

      // Cancel any previous stream
      abortRef.current?.abort();

      // G2: Auto-label current tab from first user message
      if (activeTabLabelRef.current === 'New Query' && query.trim()) {
        const tabId = activeTabIdRef.current;
        const label = query.trim().slice(0, 40);
        setTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, label } : t)));
      }

      const controller = new AbortController();
      abortRef.current = controller;

      // Close command palette when sending a query
      setShowCommandPalette(false);

      const userMsg: ChatMessage = {
        id: uid(),
        role: 'user',
        content: query,
        citations: [],
        isStreaming: false,
      };

      const assistantId = uid();
      const assistantMsg: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        citations: [],
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);
      setStreamPhase('searching');
      setActiveCitationNum(null);
      setActiveMsgId(assistantId);
      lastChunkTimeRef.current = Date.now();
      const sendTime = Date.now();
      let firstTextReceived = false;
      let citationsReceived = false;

      // A7: rAF-batched text flush — accumulate deltas in a buffer and write to
      // React state at most once per animation frame (~60fps). Streaming deltas
      // arrive faster than React can re-render the whole message list; flushing
      // per-delta caused janky "batchy" rendering. This coalesces writes smoothly.
      let pendingText = '';
      let rafId: number | null = null;
      // F45: track whether checklist mode actually produced a checklist so the
      // user gets told when it silently fell back to a standard answer.
      let gotChecklist = false;
      const flushText = () => {
        rafId = null;
        if (!pendingText) return;
        const toAppend = pendingText;
        pendingText = '';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: m.content + toAppend } : m,
          ),
        );
      };
      const scheduleFlush = () => {
        if (rafId !== null) return;
        rafId = typeof requestAnimationFrame === 'function'
          ? requestAnimationFrame(flushText)
          : (setTimeout(flushText, 16) as unknown as number);
      };
      const cancelFlush = () => {
        if (rafId !== null) {
          if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
          else clearTimeout(rafId as unknown as ReturnType<typeof setTimeout>);
          rafId = null;
        }
      };

      // Request screen wake lock
      if ('wakeLock' in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch { /* not supported or denied — ignore */ }
      }

      const releaseWakeLock = async () => {
        if (wakeLockRef.current) {
          try { await wakeLockRef.current.release(); } catch { /* ignore */ }
          wakeLockRef.current = null;
        }
      };

      try {
        await streamQuery(
          query,
          // onText (A7: buffered, flushed on rAF)
          (chunk) => {
            lastChunkTimeRef.current = Date.now();
            if (!firstTextReceived) {
              firstTextReceived = true;
              setStreamPhase('streaming');
            }
            pendingText += chunk;
            scheduleFlush();
          },
          // onCitations
          (citations) => {
            if (!citationsReceived) {
              citationsReceived = true;
              // Only advance to 'retrieving' if we haven't already started streaming text
              if (!firstTextReceived) {
                setStreamPhase('retrieving');
              }
            }
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, citations: [...m.citations, ...citations] }
                  : m,
              ),
            );
          },
          // onDone
          () => {
            // A7: flush any buffered text before finalising the message
            cancelFlush();
            const finalAppend = pendingText;
            pendingText = '';
            const elapsed = Date.now() - sendTime;
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const updated: typeof m = { ...m, content: m.content + finalAppend, isStreaming: false, elapsed };
                // Retrieval map: mark cited clauses now that the full citation list is known.
                // IMPORTANT: only update retrievalTree if some clause is ACTUALLY gaining
                // a cited flag.  Creating a new tree reference unconditionally triggers
                // useEffect([tree]) inside ReactFlowRetrievalMapInner which calls
                // multiple setNodes/setEdges/setState calls — this exceeds React's
                // re-render budget (error #310) when no clause genuinely changed.
                if (updated.retrievalTree && updated.citations.length > 0) {
                  const citedIds = new Set(updated.citations.map((c) => c.id));
                  const hasNewCited = updated.retrievalTree.clauses.some(
                    (c) => !c.cited && citedIds.has(c.id),
                  );
                  if (hasNewCited) {
                    updated.retrievalTree = {
                      ...updated.retrievalTree,
                      clauses: updated.retrievalTree.clauses.map((c) =>
                        citedIds.has(c.id) ? { ...c, cited: true } : c,
                      ),
                    };
                  }
                }
                return updated;
              }),
            );
            setIsStreaming(false);
            setStreamPhase('idle');
            setActiveQueryId(null);
            abortRef.current = null;
            void releaseWakeLock();
            // F45: checklist mode that produced no checklist used to fail in
            // total silence — surface it.
            if (checklistMode && !gotChecklist) {
              addToast('warning', 'Checklist couldn’t be generated for this query — showing the standard answer instead. Try a topic-shaped query (e.g. “heated towel rail requirements”).', 8000);
            }
          },
          // onError
          (error) => {
            // A7: flush any buffered text before finalising the message
            cancelFlush();
            const finalAppend = pendingText;
            pendingText = '';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + finalAppend, isStreaming: false, error }
                  : m,
              ),
            );
            setIsStreaming(false);
            setStreamPhase('idle');
            setActiveQueryId(null);
            abortRef.current = null;
            void releaseWakeLock();
            // Toast for specific error types
            if (error.toLowerCase().includes('session') && error.toLowerCase().includes('expired')) {
              addToast('error', 'Session expired — please sign in again', 6000);
            } else if (error.toLowerCase().includes('rate limit')) {
              addToast('warning', 'Rate limit hit — please wait before trying again', 5000);
            } else {
              addToast('error', error.length > 100 ? error.slice(0, 100) + '…' : error, 5000);
            }
          },
          // AbortSignal
          controller.signal,
          // onQueryId — C4: store queryId on the message for FeedbackBar + shareable URL
          (qid) => {
            setActiveQueryId(qid);
            // Update URL to shareable /q/{queryId}
            try {
              window.history.pushState({ queryId: qid }, '', `/q/${qid}`);
            } catch { /* ignore */ }
            // Pick up sessionId from localStorage (set by api.ts SSE handler)
            try {
              const sid = localStorage.getItem(LS_SESSION_ID_KEY);
              if (sid && !currentSessionId) setCurrentSessionId(sid);
            } catch { /* ignore */ }
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, queryId: qid } : m,
              ),
            );
            // Advance phase: we have a queryId, so corpus search is done
            if (!citationsReceived && !firstTextReceived) {
              setStreamPhase('generating');
            }
          },
          // onUsage — store usage stats on the assistant message
          (usage) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, usage } : m,
              ),
            );
          },
          // sessionId for conversation continuity
          currentSessionId,
          // onConfidence — store confidence assessment on the assistant message
          (confidence, reason) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, confidence: confidence as ChatMessage['confidence'], confidenceReason: reason ?? undefined }
                  : m,
              ),
            );
          },
          // Phase 8: plain english mode from user preferences
          userPrefs.plain_english || false,
          // Phase 8: domain filter from active chip bar
          activeDomains.length > 0 ? activeDomains : undefined,
          // Checklist mode
          checklistMode,
          // onChecklist — store structured checklist on the assistant message
          (checklist: ChecklistData) => {
            gotChecklist = true;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, checklist } : m,
              ),
            );
          },
          // G4: onCachedHit — mark message as served from cache
          () => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, cached: true } : m,
              ),
            );
          },
          // Retrieval Map: store tree on assistant message
          (tree) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, retrievalTree: tree } : m,
              ),
            );
          },
          // G5-fix: bypass G4 cache when user explicitly clicked "Ask fresh anyway"
          shouldSkipCache,
        );
      } catch {
        // A7: clean up any pending rAF flush on hard failure
        cancelFlush();
        const finalAppend = pendingText;
        pendingText = '';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: m.content + finalAppend, isStreaming: false, error: 'Failed to connect to server' }
              : m,
          ),
        );
        setIsStreaming(false);
        setStreamPhase('idle');
        setActiveQueryId(null);
        abortRef.current = null;
        addToast('error', 'Failed to connect to server', 5000);
        if (wakeLockRef.current) {
          try { await wakeLockRef.current.release(); } catch { /* ignore */ }
          wakeLockRef.current = null;
        }
      }
    },
    [researchBriefMode, handleSendResearchBrief, sbsMode, sbsSelectedJurisdictions, handleSendComparison],
  );

  /* ── Refresh bookmark count ── */
  const refreshBookmarkCount = useCallback(() => {
    setBookmarkCount(loadBookmarks().length);
  }, []);

  /* Beta banner dismiss — animate out then set sessionStorage */
  const handleBetaBannerDismiss = useCallback(() => {
    setBetaBannerDismissing(true);
    setTimeout(() => {
      sessionStorage.setItem('beta-banner-dismissed', 'true');
      setBetaBannerGone(true);
      setBetaBannerDismissing(false);
    }, 420);
  }, []);

  /* ── Highlight state management ── */
  const refreshHighlights = useCallback(() => {
    setHighlightCount(loadHighlights().length);
    setHighlightRefreshKey((k) => k + 1);
  }, []);

  /* ── Load a query by queryId (from bookmark or search) ── */
  const handleLoadQuery = useCallback(async (queryId: string) => {
    try {
      const result = await fetchQueryResult(queryId);
      if (result.status === 'complete' && result.response_text) {
        const citations: Citation[] = (result.citations ?? []).map((c, i) => ({
          id: c.id,
          number: i + 1,
          standard: c.standard,
          clause: c.clause_number ?? '',
          text: '',
          provenance: c.provenance ?? null, // G7: preserve HTML provenance for source badge
        }));
        setMessages([
          {
            id: 'bookmark-user',
            role: 'user',
            content: result.query_text || '(Bookmarked query)',
            citations: [],
            isStreaming: false,
          },
          {
            id: 'bookmark-assistant',
            role: 'assistant',
            content: result.response_text,
            citations,
            isStreaming: false,
            queryId,
            retrievalTree: result.retrieval_tree ?? null,
            confidence: (result.confidence as ChatMessage['confidence']) ?? undefined,
            confidenceReason: result.confidence_reason ?? undefined,
            corpusVersion: result.corpus_version ?? undefined,
            corpusStale: !!(result.corpus_version && result.current_corpus_version && result.corpus_version !== result.current_corpus_version),
          },
        ]);
        setActiveMsgId('bookmark-assistant');
        // Update URL
        try {
          window.history.pushState({ queryId }, '', `/q/${queryId}`);
        } catch { /* ignore */ }
      }
    } catch {
      // Query not found or error
    }
  }, []);

  /* ── Shareable URL: load query from /q/{queryId} on mount ── */
  useEffect(() => {
    const match = window.location.pathname.match(/^\/q\/([a-zA-Z0-9_-]+)$/);
    if (!match) return;
    const qid = match[1];
    (async () => {
      try {
        // F45: checklists were never replayed on /q/ deep links — the checklist
        // event only exists in the live SSE stream. Fetch the persisted one
        // (checklist id == query id) alongside the query result.
        const [result, checklist] = await Promise.all([
          fetchQueryResult(qid),
          // fetchChecklist never rejects (internal catch), but keep the guard —
          // the optional checklist must never break core answer hydration.
          fetchChecklist(qid).catch(() => null),
        ]);
        if (result.status === 'complete' && result.response_text) {
          const citations: Citation[] = (result.citations ?? []).map((c, i) => ({
            id: c.id,
            number: i + 1,
            standard: c.standard,
            clause: c.clause_number ?? '',
            text: '',
            provenance: c.provenance ?? null, // G7: preserve HTML provenance for source badge
          }));
          const hasChecklistItems = !!checklist && (checklist.categories ?? []).some(cat => (cat.items ?? []).length > 0);
          setMessages([
            {
              id: 'shared-user',
              role: 'user',
              content: result.query_text || '(Shared query)',
              citations: [],
              isStreaming: false,
            },
            {
              id: 'shared-assistant',
              role: 'assistant',
              content: result.response_text,
              citations,
              isStreaming: false,
              queryId: qid,
              checklist: hasChecklistItems ? { ...checklist!, checklistId: qid } : undefined,
              retrievalTree: result.retrieval_tree ?? null,
              confidence: (result.confidence as ChatMessage['confidence']) ?? undefined,
              confidenceReason: result.confidence_reason ?? undefined,
              corpusVersion: result.corpus_version ?? undefined,
              corpusStale: !!(result.corpus_version && result.current_corpus_version && result.corpus_version !== result.current_corpus_version),
            },
          ]);
        }
      } catch {
        // Query not found or error — just show empty state
      }
    })();
  }, []);

  /* ── Session PDF Export ── */
  const handleExportSession = useCallback(async () => {
    if (messages.length === 0) return;
    addToast('info', 'Preparing export…', 2000);
    try {
      const firstUserMsg = messages.find((m) => m.role === 'user');
      await exportSession(messages, {
        sessionTitle: firstUserMsg?.content.slice(0, 80) || 'Compliance Hub Session',
        sessionId: currentSessionId ?? undefined,
      });
      addToast('success', 'Export complete', 3000);
    } catch {
      addToast('error', 'Export failed — try again', 4000);
    }
  }, [messages, currentSessionId, addToast]);

  /* ── Fill input (example chips) ── */
  const handleFillInput = useCallback((text: string) => {
    const el = queryInputRef.current;
    if (!el) return;
    // Use native setter to trigger React's onChange
    const nativeSetter = Object.getOwnPropertyDescriptor(
      window.HTMLTextAreaElement.prototype, 'value'
    )?.set;
    if (nativeSetter) {
      nativeSetter.call(el, text);
      el.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      el.value = text;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.focus();
  }, []);

  /* ── Template save / delete handlers ── */
  const handleSaveTemplate = useCallback((query: string) => {
    const name = prompt('Name this template:', query.slice(0, 60));
    if (!name || !name.trim()) return;
    createTemplate(name.trim(), query)
      .then((tpl) => {
        setSavedTemplates((prev) => [tpl, ...prev]);
        addToast('success', `Template “${tpl.name}” saved`, 3000);
      })
      .catch((err: Error) => {
        addToast('error', err.message || 'Failed to save template', 4000);
      });
  }, [addToast]);

  const handleDeleteTemplate = useCallback((id: string) => {
    deleteTemplate(id)
      .then(() => {
        setSavedTemplates((prev) => prev.filter((t) => t.id !== id));
        addToast('success', 'Template deleted', 2000);
      })
      .catch((err: Error) => {
        addToast('error', err.message || 'Failed to delete template', 4000);
      });
  }, [addToast]);

  /* ── Command Palette action handler ── */
  const handlePaletteAction = useCallback((actionId: string) => {
    switch (actionId) {
      case 'new-chat':  handleNewChat(); break;
      case 'settings':  setShowSettings(true); break;
      case 'bookmarks': setShowResearch(true); setResearchTab('bookmarks'); break;
      case 'corpus':    setShowCorpusBrowser(true); break;
      case 'dashboard': setShowSidebar(true); break;
      case 'shortcuts': setShowShortcuts(true); break;
      case 'highlights': setShowResearch(true); setResearchTab('highlights'); break;
      case 'compare':    setShowCompare(true); break;
      case 'report':         setShowReportBuilder(true); break;
      case 'manual-builder': setShowManualBuilder(true); break;
      case 'manual-review': setShowManualReview(true); break;
      case 'resources':  setShowResourceLinks(true); break;
      case 'corpus-updates': setCorpusBrowserTab('updates'); setShowCorpusBrowser(true); break;
      case 'checklist': setChecklistMode((v) => !v); break;
      case 'research-brief': setResearchBriefMode((v) => !v); break;
      case 'history': setShowSidebar(true); break;
    }
  }, [handleNewChat]);

  /* ── Keyboard Shortcuts ── */
  useKeyboardShortcuts({
    focusInput: () => {
      const el = queryInputRef.current;
      if (el) {
        // If already focused and empty, toggle command palette
        if (document.activeElement === el && el.value.trim() === '') {
          setShowCommandPalette((v) => !v);
        } else {
          el.focus();
          // If input is empty after focus, show command palette
          if (el.value.trim() === '') {
            setShowCommandPalette(true);
          }
        }
      }
    },
    sendQuery: () => {
      const el = queryInputRef.current;
      if (!el) return;
      const value = el.value.trim();
      if (value && !isStreaming) {
        handleSend(value);
        el.value = '';
        el.style.height = 'auto';
      }
    },
    fillLastQuery: () => {
      const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
      if (lastUserMsg && queryInputRef.current) {
        queryInputRef.current.value = lastUserMsg.content;
        // Trigger auto-resize
        queryInputRef.current.style.height = 'auto';
        queryInputRef.current.style.height = `${queryInputRef.current.scrollHeight}px`;
      }
    },
    closePanel: () => {
      if (showShortcuts) setShowShortcuts(false);
      else if (showCommandPalette) setShowCommandPalette(false);
      else if (showOverflow) setShowOverflow(false);
      else if (showResearch) setShowResearch(false);
      else if (showSidebar) setShowSidebar(false);
      else if (showCorpusBrowser) setShowCorpusBrowser(false);
      else if (showSettings) setShowSettings(false);
      else if (showMobileSheet) setShowMobileSheet(false);
    },
    newChat: handleNewChat,
    isInputFocusedAndEmpty: () => {
      const el = queryInputRef.current;
      return !!el && document.activeElement === el && el.value.trim() === '';
    },
    toggleShortcuts: () => setShowShortcuts((v) => !v),
    openResearchBookmarks: () => { setShowResearch(true); setResearchTab('bookmarks'); },
    openResearchHighlights: () => { setShowResearch(true); setResearchTab('highlights'); },
    toggleSettings: () => setShowSettings((v) => !v),
    openCompare: () => setShowCompare(true),
  });

  /* ── Citation clicked (from inline badge or source card) ── */
  const handleCitationClick = useCallback(
    (msgId: string, num: number) => {
      // num === -1 signals a Retry request
      if (num === -1) {
        const idx = messages.findIndex((m) => m.id === msgId);
        if (idx > 0 && messages[idx - 1].role === 'user') {
          // Remove the errored assistant message, re-send the query
          const query = messages[idx - 1].content;
          setMessages((prev) => prev.filter((m) => m.id !== msgId));
          handleSend(query);
        }
        return;
      }

      setActiveMsgId(msgId);
      setActiveCitationNum(num);
    },
    [messages, handleSend],
  );

  /* ── Mobile: open bottom sheet for a message's sources ── */
  const handleShowMobileSources = useCallback((msgId: string) => {
    setActiveMsgId(msgId);
    setShowMobileSheet(true);
  }, []);

  const handlePanelCitationClick = useCallback((num: number) => {
    setActiveCitationNum(num);
  }, []);

  const closeMobileSheet = useCallback(() => {
    setShowMobileSheet(false);
  }, []);

  /* ── C3: bottom-sheet swipe-to-dismiss ── */
  const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
    swipeStartYRef.current = e.touches[0].clientY;
  }, []);

  const handleSheetTouchMove = useCallback((e: React.TouchEvent) => {
    const dy = e.touches[0].clientY - swipeStartYRef.current;
    if (dy > 0 && bottomSheetRef.current) {
      bottomSheetRef.current.style.transform = `translateY(${dy}px)`;
      bottomSheetRef.current.style.transition = 'none';
    }
  }, []);

  const handleSheetTouchEnd = useCallback((e: React.TouchEvent) => {
    const dy = e.changedTouches[0].clientY - swipeStartYRef.current;
    if (bottomSheetRef.current) {
      bottomSheetRef.current.style.transform = '';
      bottomSheetRef.current.style.transition = '';
      if (dy > 80) {
        closeMobileSheet();
      }
    }
  }, [closeMobileSheet]);

  /* ── Offline detection ── */
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  /* ── Theme ── */
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem('ch-theme') as Theme) || 'dark';
    } catch { return 'dark'; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme === 'system' ? '' : theme);
    try { localStorage.setItem('ch-theme', theme); } catch { /* ignore */ }
    // Re-apply accent so --accent-text updates for the new theme
    try {
      const h = parseInt(localStorage.getItem('ch-accent-h') ?? '');
      const s = parseInt(localStorage.getItem('ch-accent-s') ?? '');
      const l = parseInt(localStorage.getItem('ch-accent-l') ?? '');
      if (!isNaN(h) && !isNaN(s) && !isNaN(l)) applyAccent(h, s, l);
      else applyAccent(239, 84, 67);
    } catch { applyAccent(239, 84, 67); }
  }, [theme]);

  /* ── Skin ── */
  const [skin, setSkin] = useState<Skin>(() => {
    try {
      return (localStorage.getItem('ch-skin') as Skin) || 'synthesis';
    } catch { return 'synthesis'; }
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-skin', skin);
    try { localStorage.setItem('ch-skin', skin); } catch { /* ignore */ }
  }, [skin]);

  /* ── Restore accent on mount ── */
  useEffect(() => {
    try {
      const h = parseInt(localStorage.getItem('ch-accent-h') ?? '');
      const s = parseInt(localStorage.getItem('ch-accent-s') ?? '');
      const l = parseInt(localStorage.getItem('ch-accent-l') ?? '');
      if (!isNaN(h) && !isNaN(s) && !isNaN(l)) {
        applyAccent(h, s, l);
      } else {
        applyAccent(239, 84, 67); // default: indigo
      }
    } catch {
      applyAccent(239, 84, 67);
    }
  }, []);

  const { returnToLanding, pendingQuery, clearPendingQuery } = useLanding();

  /* ── Pick up pending query from landing page (sample question click) ── */
  useEffect(() => {
    if (pendingQuery) {
      // Small delay to ensure QueryInput ref is ready after mount
      const t = setTimeout(() => {
        handleFillInput(pendingQuery);
        clearPendingQuery();
      }, 150);
      return () => clearTimeout(t);
    }
  }, [pendingQuery, handleFillInput, clearPendingQuery]);

  /* ── Idle screensaver: DISABLED (Grant 2026-06-24) — was clearing in-progress queries ── */
  // useEffect(() => {
  //   const IDLE_MS = 5 * 60 * 1000;
  //   let timer: ReturnType<typeof setTimeout>;
  //   const resetTimer = () => { clearTimeout(timer); timer = setTimeout(() => returnToLanding(), IDLE_MS); };
  //   const events: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
  //   events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
  //   resetTimer();
  //   return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, resetTimer)); };
  // }, [returnToLanding]);

  /* ── Render ── */
  return (
    <div className="app">
      {/* Left sidebar — primary navigation per ui-review-phase2 §2 */}
      <Sidebar
        isAdminUser={isAdminUser}
        onOpenSettings={() => setShowSettings(true)}
        onOpenHelp={() => { setShowTourForce(true); setTourKey(k => k + 1); }}
        onOpenResources={() => setShowResourceLinks(true)}
        onOpenHistory={() => setShowSidebar(true)}
        onOpenShortcuts={() => setShowShortcuts(true)}
        researchBadge={bookmarkCount + highlightCount}
        hasCorpusUpdates={hasUnreadUpdates}
      />
      <div className="app-content">
      {/* Unified tab-style header — AppHeader handles health, spend, user info, sign out, nav tabs */}
      <AppHeader
        inboxCount={inboxCount}
        isAdminUser={isAdminUser}
        onOpenOverflow={() => setShowOverflow(v => !v)}
        onOpenCommandPalette={() => setShowCommandPalette(true)}
        onOpenFeedback={() => setShowFeedbackModal(true)}
      />

      {/* Mobile overflow menu */}
      {showOverflow && (
        <div className="overflow-menu">
          <button onClick={() => { setShowCorpusBrowser(true); setShowOverflow(false); }}>📚 Corpus</button>
          <button onClick={() => { setShowSettings(true); setShowOverflow(false); }}>⚙️ Settings</button>
          <button onClick={() => { setShowSidebar(true); setShowOverflow(false); }}>📋 History</button>
          <button onClick={() => { handleNewChat(); setShowOverflow(false); }}>+ New Chat</button>
          <button onClick={() => { setShowTourForce(true); setTourKey(k => k + 1); setShowOverflow(false); }}>? Tour</button>
          <a href="/user-guide.html" target="_blank" rel="noopener noreferrer" className="overflow-link" onClick={() => setShowOverflow(false)}>Getting Started</a>
          <a href="/user-guide.html" target="_blank" rel="noopener noreferrer" className="overflow-link" data-tour="user-guide-link" onClick={() => setShowOverflow(false)}>📖 User Guide</a>
        </div>
      )}

      {/* Offline banner */}
      {isOffline && (
        <div className="offline-banner" role="alert">
          You are offline. Queries require an internet connection.
        </div>
      )}

      {/* Main split pane */}
      <div className="main">
        {/* Left: Chat */}
        <div className="chat-column" style={{ flex: `0 0 ${splitRatio * 100}%` }}>
          {/* Outstanding items + amendment alerts banner */}
          {isAdminUser && <AlertsBanner />}
          {/* G2: Query tab bar */}
          <TabBar
            tabs={tabs}
            activeTabId={activeTabId}
            onSelectTab={handleSelectTab}
            onCloseTab={handleCloseTab}
            onNewTab={handleNewTab}
          />
          {/* G5: Similar query banner */}
          {similarBanner && (
            <div style={{
              margin: '8px 12px 4px',
              padding: '10px 14px',
              background: 'var(--accent-soft, rgba(99,102,241,0.12))',
              border: '1px solid var(--accent, #6366f1)',
              borderRadius: '8px',
              fontSize: 'var(--t-sm, 13px)',
              color: 'var(--ink-1, #f1f5f9)',
            }}>
              <div style={{ marginBottom: '6px' }}>
                <strong>⚡ Similar question asked recently</strong> — {similarBanner.result.query_text?.slice(0, 80)}{(similarBanner.result.query_text?.length ?? 0) > 80 ? '…' : ''}
              </div>
              {similarBanner.result.response_preview && (
                <div style={{ color: 'var(--ink-3, #94a3b8)', marginBottom: '8px', fontSize: '12px', fontStyle: 'italic' }}>
                  {similarBanner.result.response_preview.slice(0, 150)}…
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    // View cached answer: submit the same query (will hit cache with ⚡ indicator)
                    const q = similarBanner.pendingQuery;
                    setSimilarBanner(null);
                    bypassSimilarRef.current = true;
                    skipCacheRef.current = false; // explicitly want the cache
                    void handleSend(q);
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: '6px', border: 'none',
                    background: 'var(--accent, #6366f1)', color: '#fff',
                    cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                  }}
                >
                  View cached answer ⚡
                </button>
                <button
                  onClick={() => {
                    // Ask fresh anyway: bypass both client similarity check AND server cache
                    const q = similarBanner.pendingQuery;
                    setSimilarBanner(null);
                    bypassSimilarRef.current = true;
                    skipCacheRef.current = true; // tell worker to skip G4 cache
                    void handleSend(q);
                  }}
                  style={{
                    padding: '5px 12px', borderRadius: '6px',
                    border: '1px solid var(--border, #334155)',
                    background: 'transparent', color: 'var(--ink-2, #cbd5e1)',
                    cursor: 'pointer', fontSize: '12px',
                  }}
                >
                  Ask fresh anyway
                </button>
                <button
                  onClick={() => setSimilarBanner(null)}
                  style={{
                    padding: '5px 10px', borderRadius: '6px',
                    border: '1px solid var(--border, #334155)',
                    background: 'transparent', color: 'var(--ink-3, #94a3b8)',
                    cursor: 'pointer', fontSize: '12px',
                  }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <Chat
            messages={messages}
            isStreaming={isStreaming && !briefActive && !sbsActive}
            onSend={handleSend}
            onCitationClick={handleCitationClick}
            onShowMobileSources={handleShowMobileSources}
            onFollowUp={handleSend}
            onFillInput={handleFillInput}
            streamPhase={(briefActive || sbsActive) ? 'idle' : streamPhase}
            onBookmarkChange={refreshBookmarkCount}
            plainEnglish={userPrefs.plain_english}
            highlightRefreshKey={highlightRefreshKey}
            onHighlightRemoved={refreshHighlights}
          />
          {/* SBS Jurisdiction Picker — shown when compare mode is on and no active comparison */}
          {sbsMode && !sbsActive && (
            <div style={{ padding: '0 12px' }}>
              <SbsJurisdictionPicker
                groups={JURISDICTION_GROUPS}
                selected={sbsSelectedJurisdictions}
                onChange={setSbsSelectedJurisdictions}
                onCompare={() => {
                  const input = document.querySelector<HTMLTextAreaElement>('.query-input textarea');
                  const q = input?.value?.trim();
                  if (q && sbsSelectedJurisdictions.length >= 2) handleSendComparison(q);
                }}
                disabled={isStreaming}
                onDismiss={() => setSbsMode(false)}
              />
            </div>
          )}
          {/* SBS Comparison View — rendered below chat when active */}
          {sbsActive && sbsState && (
            <div style={{ padding: '0 12px 12px' }}>
              <SbsComparisonView
                state={sbsState}
                onCitationClick={(cit) => {
                  // SBS citation click: use citation number to focus panel
                  setActiveCitationNum(cit.number);
                }}
                onClose={() => { setSbsActive(false); setSbsState(null); }}
              />
            </div>
          )}
          {/* Research Brief View — rendered below chat messages when active */}
          {briefActive && (
            <div className="rb-overlay-container">
              <ResearchBriefView
                sections={briefSections}
                executiveSummary={briefExecutiveSummary}
                progress={briefProgress}
                meta={briefMeta}
                error={briefError}
                onStop={handleStop}
                onClose={() => setBriefActive(false)}
              />
            </div>
          )}
        </div>

        <ResizeDivider ratio={splitRatio} onRatioChange={setSplitRatio} min={0.25} max={0.75} />

        {/* Right: Citation panel */}
        <div className="citation-column" style={{ flex: 1 }}>
          <CitationPanel
            citations={panelCitations}
            activeCitationNum={activeCitationNum}
            onCitationClick={handlePanelCitationClick}
            queryText={panelQueryText}
            retrievalTree={panelRetrievalTree}
            isStreaming={isStreaming}
          />
        </div>
      </div>

      {/* ── Query input area (toolbar-kill: bottom toolbar removed, tools absorbed into sidebar) ── */}
      <div style={{ position: 'relative' }}>
        <CommandPalette
          open={showCommandPalette}
          onClose={() => setShowCommandPalette(false)}
          onAction={(id) => handlePaletteAction(id)}
          onFillInput={handleFillInput}
          savedTemplates={savedTemplates}
          onDeleteTemplate={handleDeleteTemplate}
        />
        <QueryInput
          ref={queryInputHandleRef}
          onSend={handleSend}
          onStop={handleStop}
          disabled={isStreaming}
          isStreaming={isStreaming}
          activeDomains={activeDomains}
          onDomainsChange={setActiveDomains}
          plainEnglish={userPrefs.plain_english}
          checklistMode={checklistMode}
          onChecklistModeChange={setChecklistMode}
          researchBriefMode={researchBriefMode}
          onResearchBriefModeChange={setResearchBriefMode}
          onSendAsResearchBrief={handleSendResearchBrief}
          sbsMode={sbsMode}
          onSbsModeChange={setSbsMode}
          onNewChat={handleNewChat}
          onSaveTemplate={handleSaveTemplate}
        />
      </div>

      {/* ResourceLinks — sidebar-driven fixed overlay (no toolbar button) */}
      <ResourceLinks
        hideButton
        forceOpen={showResourceLinks}
        onForceOpenConsumed={() => setShowResourceLinks(false)}
      />

      {/* Mobile bottom sheet */}
      <div
        className={`bottom-sheet-overlay ${showMobileSheet ? 'bottom-sheet-overlay--visible' : ''}`}
        onClick={closeMobileSheet}
        aria-hidden={!showMobileSheet}
      />
      <div
        ref={bottomSheetRef}
        className={`bottom-sheet ${showMobileSheet ? 'bottom-sheet--open' : ''}`}
        role="dialog"
        aria-label="Citation sources"
        aria-hidden={!showMobileSheet}
        onTouchStart={handleSheetTouchStart}
        onTouchMove={handleSheetTouchMove}
        onTouchEnd={handleSheetTouchEnd}
      >
        <div className="bottom-sheet-handle-bar">
          <div className="bottom-sheet-handle" />
        </div>
        <button
          className="bottom-sheet-close"
          onClick={closeMobileSheet}
          aria-label="Close sources panel"
        >
          ✕
        </button>
        <CitationPanel
          citations={panelCitations}
          activeCitationNum={activeCitationNum}
          onCitationClick={handlePanelCitationClick}
          queryText={panelQueryText}
          retrievalTree={panelRetrievalTree}
          isStreaming={isStreaming}
        />
      </div>
      {/* Corpus Browser Modal */}
      {showCorpusBrowser && (
        <CorpusBrowser onClose={() => { setShowCorpusBrowser(false); setCorpusBrowserTab('browse'); }} initialTab={corpusBrowserTab} />
      )}

      {/* Settings Sidebar */}
      <Settings
        isOpen={showSettings}
        theme={theme}
        onThemeChange={setTheme}
        skin={skin}
        onSkinChange={setSkin}
        onClose={() => setShowSettings(false)}
        onPreferencesChange={handlePreferencesChange}
      />

      {/* Session Sidebar */}
      <SessionSidebar
        isOpen={showSidebar}
        onClose={() => setShowSidebar(false)}
        currentSessionId={currentSessionId}
        onLoadSession={handleLoadSession}
        onNewChat={handleNewChat}
        userId={userId}
        onExportSession={handleExportSession}
        hasMessages={messages.length > 0}
        onLoadQuery={handleLoadQuery}
        onOpenBookmarks={() => { setShowSidebar(false); setShowResearch(true); setResearchTab('bookmarks'); }}
        bookmarkCount={bookmarkCount}
      />

      {/* Onboarding Tour */}
      <OnboardingTour
        key={tourKey}
        forceShow={showTourForce}
        onComplete={() => setShowTourForce(false)}
      />

      {/* Research Panel (merged Bookmarks + Highlights) */}
      {showResearch && (
        <>
          <div
            className="sidebar-backdrop sidebar-backdrop--visible"
            onClick={() => setShowResearch(false)}
          />
          <aside className="session-sidebar session-sidebar--open" aria-label="Research">
            <ResearchPanel
              activeTab={researchTab}
              onTabChange={setResearchTab}
              onLoadBookmark={(qid) => { handleLoadQuery(qid); setShowResearch(false); }}
              onJumpToMessage={(msgId) => {
                setShowResearch(false);
                const el = document.querySelector(`[data-msg-id="${msgId}"]`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.classList.add('highlight-flash');
                  setTimeout(() => el.classList.remove('highlight-flash'), 1500);
                }
              }}
              onClose={() => setShowResearch(false)}
              onOpenReportBuilder={() => { setShowResearch(false); setShowReportBuilder(true); }}
              refreshKey={highlightRefreshKey}
              onHighlightsChanged={refreshHighlights}
              onBookmarkChange={refreshBookmarkCount}
            />
          </aside>
        </>
      )}

      {/* Text Selection Toolbar (floating, portal-like) */}
      <SelectionToolbar onHighlightAdded={refreshHighlights} />

      {/* Edition Compare Modal */}
      {showCompare && (
        <CompareView onClose={() => setShowCompare(false)} />
      )}

      {/* Report Builder */}
      {showManualBuilder && (
        <ManualBuilder onClose={() => setShowManualBuilder(false)} />
      )}
      {/* Manual Review */}
      {showManualReview && (
        <ManualReview onClose={() => setShowManualReview(false)} />
      )}
      {showReportBuilder && (
        <ReportBuilder onClose={() => setShowReportBuilder(false)} />
      )}

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcuts
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
      />

      {/* B1: Admin Ingestion Inbox Modal */}
      {isAdminUser && (
        <InboxModal
          isOpen={showInbox}
          onClose={() => setShowInbox(false)}
          onCountChange={setInboxCount}
        />
      )}

      {/* B4: Admin Cost Dashboard slide-in */}
      {isAdminUser && showCostDashboard && (
        <>
          <div
            className="sidebar-backdrop sidebar-backdrop--visible"
            onClick={() => setShowCostDashboard(false)}
          />
          <aside className="session-sidebar session-sidebar--open" aria-label="Cost Dashboard" style={{ zIndex: 500 }}>
            <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 600, fontSize: '15px' }}>Cost Dashboard</span>
              <button onClick={() => setShowCostDashboard(false)} aria-label="Close cost dashboard" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '16px' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <CostDashboard isVisible={showCostDashboard} />
            </div>
          </aside>
        </>
      )}


      {/* F2: Admin Analytics Dashboard slide-in */}
      {isAdminUser && showAnalyticsDashboard && (
        <>
          <div
            className="sidebar-backdrop sidebar-backdrop--visible"
            onClick={() => setShowAnalyticsDashboard(false)}
          />
          <aside className="session-sidebar session-sidebar--open" aria-label="Analytics Dashboard" style={{ zIndex: 500 }}>
            <div style={{ padding: '16px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 600, fontSize: '15px' }}>Analytics Dashboard</span>
              <button onClick={() => setShowAnalyticsDashboard(false)} aria-label="Close analytics dashboard" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '16px' }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              <AnalyticsDashboard isVisible={showAnalyticsDashboard} />
            </div>
          </aside>
        </>
      )}
      {/* Beta banner modal — shown on first session load */}
      {!betaBannerGone && (
        <div
          className={`beta-modal-overlay${betaBannerDismissing ? ' beta-modal-overlay--dismissing' : ''}`}
          onClick={handleBetaBannerDismiss}
          role="dialog"
          aria-modal="true"
          aria-label="Beta notice"
        >
          <div
            className={`beta-modal-card${betaBannerDismissing ? ' beta-modal-card--dismissing' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="beta-modal-title">Compliance Hub — Beta</h3>
            <p className="beta-modal-body">
              Compliance Hub is in beta and actively being developed — you will likely notice frequent changes and additions, and many issues are still being worked out.
            </p>
            <div className="beta-modal-actions">
              <button
                className="header-beta-feedback-btn header-beta-feedback-btn--amber"
                onClick={() => { handleBetaBannerDismiss(); setShowFeedbackModal(true); }}
              >
                Submit Feedback →
              </button>
              <button className="beta-modal-ok-btn" onClick={handleBetaBannerDismiss}>
                OK, got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* General Feedback Modal */}
      {showFeedbackModal && (
        <>
          <div
            className="sidebar-backdrop sidebar-backdrop--visible"
            onClick={() => { setShowFeedbackModal(false); setFeedbackText(''); setFeedbackDone(false); }}
          />
          <div className="feedback-modal" role="dialog" aria-label="Submit Feedback">
            <div className="feedback-modal-header">
              <span className="feedback-modal-title">💬 Submit Feedback</span>
              <button
                className="feedback-modal-close"
                onClick={() => { setShowFeedbackModal(false); setFeedbackText(''); setFeedbackDone(false); }}
                aria-label="Close"
              >✕</button>
            </div>
            {feedbackDone ? (
              <div className="feedback-modal-body" style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
                <div style={{ fontWeight: 600 }}>Thanks for the feedback!</div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>We\'ll review it shortly.</div>
              </div>
            ) : (
              <div className="feedback-modal-body">
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  Tell us what\'s working, what\'s not, or what you\'d like to see.
                </p>
                <textarea
                  className="feedback-modal-textarea"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Your feedback..."
                  rows={5}
                  maxLength={1000}
                  autoFocus
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{feedbackText.length}/1000</span>
                  <button
                    className="feedback-modal-submit"
                    disabled={!feedbackText.trim() || feedbackSubmitting}
                    onClick={async () => {
                      setFeedbackSubmitting(true);
                      try {
                        await submitIssue({
                          category: 'other',
                          severity: 'minor',
                          description: feedbackText.trim().slice(0, 1000),
                        });
                        setFeedbackDone(true);
                        setTimeout(() => { setShowFeedbackModal(false); setFeedbackText(''); setFeedbackDone(false); }, 3000);
                      } catch { /* silent */ }
                      setFeedbackSubmitting(false);
                    }}
                  >
                    {feedbackSubmitting ? 'Sending...' : 'Submit'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div> {/* .app-content */}
    </div>
  );
}

const ToolPageFallback = (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--ink-3, #94a3b8)', fontSize: '14px' }}>
    Loading…
  </div>
);

/**
 * Wrapper that always keeps <MainApp /> mounted but hides it when at
 * portal / tool / research routes. This preserves retrieval-map state
 * (and all other React state) so the map never disappears on navigation.
 */
function PersistentMainApp() {
  const location = useLocation();
  const hide =
    location.pathname.startsWith('/portal') ||
    location.pathname.startsWith('/tools') ||
    location.pathname === '/research';
  // display:contents when visible → wrapper div is layout-transparent (no box, no scroll impact).
  // display:none when at portal/tool/research routes → hides MainApp without unmounting it.
  return (
    <div style={{ display: hide ? 'none' : 'contents' }}>
      <MainApp />
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AuthGate>
        {/* MainApp is ALWAYS mounted — CSS hides it at portal/tool/research routes.
            This preserves retrieval-map state across navigation. */}
        <PersistentMainApp />
        <Routes>
          <Route path="/portal/*" element={<PortalRoutes />} />
          {/* Tool pages — promoted from modals, wrapped in ToolLayout for sidebar+header */}
          <Route
            path="/tools/report-builder"
            element={
              <ToolLayout>
                <Suspense fallback={ToolPageFallback}><ReportBuilderPage /></Suspense>
              </ToolLayout>
            }
          />
          <Route
            path="/tools/copy-builder"
            element={
              <ToolLayout>
                <Suspense fallback={ToolPageFallback}><CopyBuilderPage /></Suspense>
              </ToolLayout>
            }
          />
          <Route
            path="/tools/manual-review"
            element={
              <ToolLayout>
                <Suspense fallback={ToolPageFallback}><ManualReviewPage /></Suspense>
              </ToolLayout>
            }
          />
          <Route
            path="/tools/corpus-browser"
            element={
              <ToolLayout>
                <Suspense fallback={ToolPageFallback}><CorpusBrowserPage /></Suspense>
              </ToolLayout>
            }
          />
          <Route
            path="/research"
            element={
              <ToolLayout>
                <Suspense fallback={ToolPageFallback}><ResearchPage /></Suspense>
              </ToolLayout>
            }
          />
        </Routes>
      </AuthGate>
      <ToastContainer />
    </ToastProvider>
  );
}
